import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import InputMask from 'react-input-mask';
import Header from '../../components/Header';
import api from '../../services/api';
import { showError, showSuccess } from '../../services/alert';

export default function ClienteForm() {
  const { id } = useParams();
  const isEdit = Boolean(id && id !== 'novo');
  const navigate = useNavigate();

  const onlyDigits = (v) => (v || '').toString().replace(/\D/g, '');

  const [cliente, setCliente] = useState({
    nome: '',
    documento: '',
    telefone: '',
    email: '',
    endereco: '',

    // ===== NOVOS CAMPOS NF-e (Destinatário) =====
    inscricaoEstadual: '',
    indicadorIe: 9,

    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    uf: '',
    municipio: '',
    codigoMunicipioIbge: '',

    codigoPais: '1058',
    nomePais: 'BRASIL'
  });

  const [documentType, setDocumentType] = useState('cpf');
  const mask = documentType === 'cpf'
    ? '999.999.999-99'
    : '99.999.999/9999-99';

  useEffect(() => {
    if (isEdit) {
      api.get(`/cliente/${id}`)
        .then(res => {
          const data = res.data || {};
          const numbers = (data.documento || '').replace(/\D/g, '');
          setDocumentType(numbers.length === 11 ? 'cpf' : 'cnpj');

          setCliente(prev => ({
            ...prev,
            ...data,

            // defaults se vier null
            indicadorIe: data.indicadorIe ?? 9,
            codigoPais: data.codigoPais || '1058',
            nomePais: data.nomePais || 'BRASIL'
          }));
        })
        .catch(() => showError('Erro ao carregar cliente.'));
    }
  }, [id, isEdit]);

  const handleChange = e => {
    const { name, value } = e.target;

    // Normalizações leves sem quebrar seu padrão
    if (name === 'cep') {
      const v = onlyDigits(value).slice(0, 8);
      setCliente(prev => ({ ...prev, [name]: v }));
      return;
    }

    if (name === 'codigoMunicipioIbge') {
      const v = onlyDigits(value).slice(0, 7);
      setCliente(prev => ({ ...prev, [name]: v }));
      return;
    }

    if (name === 'uf') {
      const v = (value || '').toUpperCase().slice(0, 2);
      setCliente(prev => ({ ...prev, [name]: v }));
      return;
    }

    setCliente(prev => ({ ...prev, [name]: value }));
  };

  const handleDocumentTypeChange = e => {
    setDocumentType(e.target.value);
    setCliente(prev => ({ ...prev, documento: '' }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    try {
      // monta payload limpando documento / defaults de país
      const payload = {
        ...cliente,
        documento: onlyDigits(cliente.documento),

        cep: onlyDigits(cliente.cep).slice(0, 8),
        codigoMunicipioIbge: onlyDigits(cliente.codigoMunicipioIbge).slice(0, 7),

        indicadorIe: Number(cliente.indicadorIe ?? 9),
        codigoPais: '1058',
        nomePais: 'BRASIL'
      };

      const res = isEdit
        ? await api.put(`/cliente/${id}`, payload)
        : await api.post('/cliente', payload);

      showSuccess(res.data?.message || 'Operação realizada com sucesso.');
      navigate('/cliente');
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao salvar cliente.');
    }
  };

  return (
    <div className="container mt-4">
      <Header />
      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex align-items-center gap-3">
          <button
            className="btn btn-light btn-sm d-flex align-items-center gap-1"
            onClick={() => navigate('/cliente')}
            type="button"
          >
            <i className="bi bi-arrow-left text-success"></i>
            <span className="text-success fw-semibold">Voltar</span>
          </button>
          <h5 className="mb-0">
            {isEdit ? 'Editar Cliente' : 'Novo Cliente'}
          </h5>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Nome</label>
              <input
                type="text"
                name="nome"
                value={cliente.nome}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Tipo Documento</label>
              <select
                className="form-select"
                value={documentType}
                onChange={handleDocumentTypeChange}
                required
              >
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Documento</label>
              <InputMask
                mask={mask}
                value={cliente.documento}
                onChange={handleChange}
                maskPlaceholder={null}
              >
                {() => (
                  <input
                    type="text"
                    name="documento"
                    className="form-control"
                    required
                  />
                )}
              </InputMask>
            </div>

            <div className="col-md-6">
              <label className="form-label">Telefone</label>
              <InputMask
                mask="(99) 99999-9999"
                value={cliente.telefone}
                onChange={handleChange}
                maskPlaceholder={null}
              >
                {() => (
                  <input
                    type="text"
                    name="telefone"
                    className="form-control"
                  />
                )}
              </InputMask>
            </div>

            <div className="col-md-6">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={cliente.email}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-12">
              <label className="form-label">Endereço (texto antigo)</label>
              <input
                type="text"
                name="endereco"
                value={cliente.endereco}
                onChange={handleChange}
                className="form-control"
              />
              <small className="text-muted">
                Mantido para compatibilidade. A NF-e usa os campos abaixo.
              </small>
            </div>

            <hr className="my-2" />

            <div className="col-12">
              <h6 className="text-success fw-semibold mb-0">Dados NF-e (Destinatário)</h6>
              <small className="text-muted">
                Preencha para evitar rejeições (enderDest + indIEDest).
              </small>
            </div>

            <div className="col-md-4">
              <label className="form-label">Inscrição Estadual (IE)</label>
              <input
                type="text"
                name="inscricaoEstadual"
                value={cliente.inscricaoEstadual || ''}
                onChange={handleChange}
                className="form-control"
                placeholder="Opcional / obrigatório se contribuinte"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Indicador IE (indIEDest)</label>
              <select
                className="form-select"
                name="indicadorIe"
                value={cliente.indicadorIe ?? 9}
                onChange={handleChange}
              >
                <option value={1}>1 - Contribuinte ICMS</option>
                <option value={2}>2 - Contribuinte Isento</option>
                <option value={9}>9 - Não Contribuinte</option>
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label">CEP</label>
              <InputMask
                mask="99999-999"
                value={cliente.cep || ''}
                onChange={(e) => {
                  // mantém máscara mas salva só números
                  const raw = onlyDigits(e.target.value);
                  setCliente(prev => ({ ...prev, cep: raw.slice(0, 8) }));
                }}
                maskPlaceholder={null}
              >
                {() => (
                  <input
                    type="text"
                    name="cep"
                    className="form-control"
                    placeholder="Somente números"
                  />
                )}
              </InputMask>
            </div>

            <div className="col-md-6">
              <label className="form-label">Logradouro</label>
              <input
                type="text"
                name="logradouro"
                value={cliente.logradouro || ''}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">Número</label>
              <input
                type="text"
                name="numero"
                value={cliente.numero || ''}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Complemento</label>
              <input
                type="text"
                name="complemento"
                value={cliente.complemento || ''}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Bairro</label>
              <input
                type="text"
                name="bairro"
                value={cliente.bairro || ''}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">UF</label>
              <input
                type="text"
                name="uf"
                value={cliente.uf || ''}
                onChange={handleChange}
                className="form-control"
                placeholder="Ex: MT"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Município</label>
              <input
                type="text"
                name="municipio"
                value={cliente.municipio || ''}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Código IBGE Município (cMun)</label>
              <input
                type="text"
                name="codigoMunicipioIbge"
                value={cliente.codigoMunicipioIbge || ''}
                onChange={handleChange}
                className="form-control"
                placeholder="7 dígitos (ex: 5103403)"
              />
            </div>

            <div className="col-12 d-flex justify-content-end">
              <button type="submit" className="btn btn-success me-2">Salvar</button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/cliente')}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
